import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, map, Observable } from 'rxjs';

import { PropsOf } from '@blaze-runtime/engine-domain';
import { DurationColumnFormat, NumberPrecision } from '@blaze-shared/api-contracts-domain';
import {
  ElementVerticalAlignment,
  getBackground,
  InputElementMetaUiFeatures,
  makeFontStyles,
  makeFontStyleStyles,
  TableColumnFormat,
} from '@blaze-shared/elements-domain';
import { getDataValue } from '@blaze-shared/schema-domain';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import { DateFormatOptions, DateTimeFormatOptions, isNullish } from '@blaze-shared/shared-util';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { CanvasElementBaseComponent } from '../__base/base.component';

// TODO: test
@UntilDestroy()
@Component({
  selector: 'be-text-field',
  templateUrl: './text-field.component.html',
  styleUrls: ['./text-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TextFieldComponent
  extends CanvasElementBaseComponent<'textField'>
  implements CanvasElementComponent<'textField'>, OnChanges, OnInit
{
  @Input() value?: string;

  private dataValue = this.getDataInput('value');

  labelEnabled!: boolean;
  labelText!: string;

  placeholder!: string;
  format!: TableColumnFormat;
  precision?: NumberPrecision;
  currency?: string;
  duration?: DurationColumnFormat;
  dateFormat?: DateFormatOptions;
  dateTimeFormat?: DateTimeFormatOptions;
  culture?: string;

  styles!: Record<string, string>;
  inputWrapperStyles!: Record<string, string>;
  inputContainerClasses$: Observable<string[]>;
  contentStyles!: Record<string, string>;
  isNullishFn = isNullish;
  isArrayValue = false;
  labelVerticalAlign: ElementVerticalAlignment | undefined;

  constructor() {
    super();

    /**
     * Allow element to grow based on height and format settings.
     * When Format is paragraph and height mode set to "min" (label describes it as Full)
     * element can grow based on its content.
     */
    this.inputContainerClasses$ = combineLatest({
      props: this.props$,
      element: this.element$,
    }).pipe(
      map(({ props, element }) => {
        const isFixed = !!element.height?.fixed;
        const overflow = props?.content?.container?.overflow;
        const isTopLabel = props?.content?.title?.position === 'top';
        const classes: string[] = [isTopLabel && !this.hasBackground(props) ? 'py-8 pr-8' : 'p-8'];
        if (isFixed) {
          classes.push('mode-fixed');
        } else {
          classes.push('mode-full');
        }
        if (overflow === 'hidden') {
          classes.push('mode-clip');
        } else {
          classes.push('mode-scroll');
        }
        return classes;
      }),
    );

    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['props']) {
      const { title, input } = this.props?.content || {};

      this.labelEnabled = title?.enabled ?? true;
      this.placeholder = input?.placeholder || '';

      this.updateStyles();
    }
    if (changes?.['value']) {
      this.value = getDataValue(this.value as any);
      this.isArrayValue = false;
      if (Array.isArray(this.value)) {
        this.value = this.value.join(', ');
        this.isArrayValue = true;
      }
    }
  }

  ngOnInit(): void {
    this.getElementUiFeatures<InputElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.labelText = features.title ?? '';
        this.precision = features.precision;
        this.format = features.format || 'text';
        this.currency = features.currency;
        this.duration = features.duration;
        this.dateFormat = features.dateFormat;
        this.dateTimeFormat = features.dateTimeFormat;
        this.culture = features.column?.format?.culture || undefined;

        this.updateStyles();
        safeDetectChanges(this.cdr);
      });
  }

  onValueClick(): void {
    switch (this.format) {
      case 'url':
        return this.dataTypeActions.openPage(this.value);
      case 'email':
        return this.dataTypeActions.copy(this.value);
      default:
        return;
    }
  }

  reset(): void {
    this.value = undefined;
    safeDetectChanges(this.cdr);
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { title, container } = content || {};
    const { general, input, inputFont, labelBlock } = style || {};
    if (!style || !title || !general || !input || !inputFont || !container || !labelBlock) {
      return;
    }

    const styles: Record<string, string> = {};
    const inputWrapperStyles: Record<string, string> = {
      background: getBackground(input.fill),
      color: input.value.color1,
    };
    const contentStyles: Record<string, string> = {
      ...makeFontStyles(inputFont, { lineHeight: this.format !== 'paragraph' }),
      ...makeFontStyleStyles(inputFont.style),
      ['font-weight']: this.fontsService.getFontWeight(inputFont.fontFamily, inputFont.fontWeight),
    };

    if (title.enabled !== false) {
      if (title.position === 'top') {
        styles['flex-direction'] = 'column';
        inputWrapperStyles['padding-left'] = '0px';
        inputWrapperStyles['padding-right'] = '0px';
      } else {
        inputWrapperStyles['flex-basis'] = `${100 - parseInt(title.width, 10)}%`;
      }
    }

    this.labelVerticalAlign = undefined;
    if (this.format === 'paragraph') {
      if (title.position === 'left') {
        this.labelVerticalAlign = title.align?.vertical;
      }
      contentStyles['white-space'] = 'pre-wrap';
    } else {
      contentStyles['margin-top'] = 'auto';
      contentStyles['margin-bottom'] = 'auto';
    }

    if (input.border.selectorType !== 'none') {
      inputWrapperStyles['border'] = `1px solid ${input.border.color1}`;
    }
    if (input.radius !== '0px') {
      inputWrapperStyles['border-radius'] = input.radius;
    }

    this.styles = styles;
    this.inputWrapperStyles = inputWrapperStyles;
    this.contentStyles = contentStyles;
  }

  private hasBackground(props: PropsOf<'textField'> | undefined): boolean {
    const { input } = props?.style || {};
    return !!(
      (input?.border && input?.border.selectorType !== 'none') ||
      (input?.fill && input?.fill.selectorType !== 'none')
    );
  }
}
